/**
 * Accessibility Section Component
 * Displays accessibility and navigation information with collapsible cards
 * Matches Figma design: Collapsible cards with yellow arrows (up when expanded, down when collapsed)
 */

import React, { useState } from 'react';
import Image from 'next/image';
import {
  typographyColors,
  backgroundColors,
  spacing,
  borders,
  typography,
  fontPrimary,
  brandColors,
} from '@/design-system/utils';
import { SectionHeader } from '@/components/ui/section-header';

export interface AccessibilityItem {
  id: string;
  heading: string;
  description: string;
  expanded?: boolean;
}

export interface AccessibilitySectionProps {
  title?: string;
  items: AccessibilityItem[];
}

/**
 * Yellow Arrow Button Component
 * Matches Figma design - yellow square button with rounded corners containing black arrow
 * @param isExpanded - true for upward arrow, false for downward arrow
 */
const YellowArrowButton: React.FC<{ isExpanded: boolean }> = ({
  isExpanded,
}) => {
  return (
    <div
      style={{
        width: '32px',
        height: '32px',
        backgroundColor: brandColors.primary,
        borderRadius: borders.radius.md,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <svg
        width='20'
        height='20'
        viewBox='0 0 16 16'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        style={{
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
        }}
      >
        <path
          d='M4 6L8 10L12 6'
          stroke={typographyColors.primary}
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    </div>
  );
};

/**
 * Accessibility Description Component
 * Displays full description text when expanded
 */
const AccessibilityDescription: React.FC<{ description: string }> = ({
  description,
}) => {
  return (
    <div
      // Prevent any clicks inside from collapsing the card
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[1],
        marginTop: spacing[3], // Padding between header and content
        cursor: 'default',
      }}
    >
      <p
        style={{
          fontFamily: fontPrimary,
          fontSize: typography.size.sm,
          fontWeight: typography.weight.regular,
          color: typographyColors.text,
          lineHeight: typography.lineHeight.relaxed,
          margin: 0,
        }}
      >
        {description}
      </p>
    </div>
  );
};

/**
 * Person Walking Icon Component
 * For "Who can Travel?" sub-section - person with walking stick
 */
const PersonWalkingIcon: React.FC = () => {
  return (
    <Image
      src='/assets/images/icons/who_can_travel.svg'
      alt='Who can travel icon'
      width={20}
      height={20}
      style={{
        flexShrink: 0,
        width: '20px',
        height: '20px',
        aspectRatio: '1/1',
      }}
    />
  );
};

export const AccessibilitySection: React.FC<AccessibilitySectionProps> = ({
  title = 'Accessibility & Navigation',
  items,
}) => {
  // Check if item is "Who can Travel?" to filter it out from collapsible items
  const isWhoCanTravel = (heading: string) => {
    return heading.toLowerCase().includes('who can travel');
  };

  // Filter out "Who can Travel?" item - it's just a header, not a collapsible card
  const collapsibleItems = items.filter(item => !isWhoCanTravel(item.heading));

  // Start with all cards collapsed by default
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleItem = (id: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        width: '100%',
      }}
    >
      <SectionHeader title={title} />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: `0 ${spacing[3]}px ${spacing[3]}px`,
        }}
      >
        {/* "Who can Travel?" Sub-section Header - Always shown */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: spacing[2], // Smaller gap between heading and cards
            marginTop: spacing[4], // Space between main title and subtitle
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing[2],
            }}
          >
            <PersonWalkingIcon />
            <h4
              style={{
                fontFamily: fontPrimary,
                fontSize: typography.size.base,
                fontWeight: typography.weight.semibold,
                color: typographyColors.primary,
                margin: 0,
              }}
            >
              Who can Travel?
            </h4>
          </div>

          {/* Collapsible Cards */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing[2], // Small gap between cards
              marginTop: spacing[3], // Space between subtitle and cards
            }}
          >
            {collapsibleItems.map(item => {
              const isExpanded = expandedItems.has(item.id);
              return (
                <div
                  key={item.id}
                  style={{
                    backgroundColor: backgroundColors.primary, // Pure white background
                    borderRadius: borders.radius.lg,
                    padding: spacing[4], // 16px padding
                    cursor: 'pointer',
                    boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.08)', // Subtle, soft drop shadow
                  }}
                  onClick={() => toggleItem(item.id)}
                >
                  {/* Header with title and yellow arrow button */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <h5
                      style={{
                        fontFamily: fontPrimary,
                        fontSize: typography.size.base,
                        fontWeight: typography.weight.semibold,
                        color: typographyColors.primary,
                        margin: 0,
                      }}
                    >
                      {item.heading}
                    </h5>
                    <YellowArrowButton isExpanded={isExpanded} />
                  </div>

                  {/* Content - No separator line, just padding */}
                  {isExpanded && (
                    <AccessibilityDescription description={item.description} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
